import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { downloadAllQRCodesByFolderId } from '~/api/qrCodeFolders';
import DocumentIcon from '~/assets/icons/document.svg?react';
import ImageIcon from '~/assets/icons/image.svg?react';
import VectorIcon from '~/assets/icons/vector.svg?react';
import DownloadQRModalContentLayout from '~/components/download/DownloadQRModalContentLayout';
import DownloadQRTypeButton from '~/components/download/DownloadQRTypeButton';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import { DownloadQrImageFormat } from '~/types/qrCode';
import { downloadQRs } from '~/utils/download';
import { logger } from '~/utils/logger';

type DownloadFolderQRCodesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: number;
  folderName: string;
};

export default function DownloadFolderQRCodesModal({
  isOpen,
  onClose,
  folderId,
  folderName,
}: DownloadFolderQRCodesModalProps) {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<null | DownloadQrImageFormat>(null);

  const imageFormats = [
    { type: 'jpeg', icon: <ImageIcon />, text: t('downloadModal:jpeg') },
    { type: 'png', icon: <ImageIcon />, text: t('downloadModal:png') },
    { type: 'svg', icon: <VectorIcon />, text: t('downloadModal:svg') },
    { type: 'eps', icon: <VectorIcon />, text: t('downloadModal:eps') },
    { type: 'pdf', icon: <DocumentIcon />, text: t('downloadModal:pdf') },
  ] as const;

  const { mutate: downloadAllQRCodesByFolderIdMutate, isPending: isPendingDownload } = useMutation({
    mutationFn: async (imgFormat: DownloadQrImageFormat) => {
      try {
        const response = await downloadAllQRCodesByFolderId(folderId, imgFormat);
        return downloadQRs({ response, fileName: `${folderName} - QR codes.zip` });
      } catch (error) {
        logger.log(error);
      }
    },
  });

  return (
    <ConfirmModal onClose={onClose} open={isOpen}>
      <DownloadQRModalContentLayout isLoading={isPendingDownload}>
        {imageFormats.map(({ type, icon, text }) => {
          return (
            <DownloadQRTypeButton
              key={type}
              type={type}
              icon={icon}
              text={text}
              onClick={() => {
                setSelectedType(type);
                downloadAllQRCodesByFolderIdMutate(type);
              }}
              isLoading={isPendingDownload && type === selectedType}
            />
          );
        })}
      </DownloadQRModalContentLayout>
    </ConfirmModal>
  );
}
